/**
 * Relative time formatting for work-item rows.
 */
import type { WorkItem } from "../../../experience-shell/workflows";
import { isWorkItemLockedAt } from "../workItemLock";

export interface DueLabel {
  readonly text: string;
  readonly variant: "warning" | "danger";
}

export function formatDueDate(iso: string, now: Date): DueLabel {
  const due = new Date(iso);
  // Due dates carry hour and minute precision, so "overdue" is measured
  // against the current instant, not the start of today.
  const isOverdue = due.getTime() < now.getTime();
  const formatted = due.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return {
    text: isOverdue ? `OVERDUE ${formatted} · ${formatOverdueBy(due, now)}` : `DUE ${formatted}`,
    variant: isOverdue ? "danger" : "warning",
  };
}

// How far past its due date an item is, as a "by ..." suffix. Uses the
// largest single unit: minutes, then hours, then days.
function formatOverdueBy(due: Date, now: Date): string {
  const minutes = Math.floor((now.getTime() - due.getTime()) / 60_000);
  if (minutes < 1) return "by less than a minute";
  if (minutes < 60) return `by ${String(minutes)} ${minutes === 1 ? "minute" : "minutes"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `by ${String(hours)} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.floor(hours / 24);
  return `by ${String(days)} ${days === 1 ? "day" : "days"}`;
}

export function relativeTime(iso: string, now: Date): string {
  const created = new Date(iso);
  const diffMs = now.getTime() - created.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 30) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${String(diffMin)}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${String(diffHr)}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${String(diffDay)}d ago`;
  return created.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Every `now`-derived label of a work-item row, folded into one string. Two
 * instants give the same fingerprint when the row would render the same, so
 * a memoized row can skip a clock tick that changes none of its labels.
 */
export function workItemTimeFingerprint(
  item: Pick<WorkItem, "createdAt" | "dueDate" | "lockedBy" | "lockedUntil">,
  now: Date,
): string {
  const due = item.dueDate !== null ? formatDueDate(item.dueDate, now).text : "";
  return `${relativeTime(item.createdAt, now)}|${due}|${String(isWorkItemLockedAt(item, now))}`;
}
